import { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../auth/useAuth';
import { fetchIssues, createSubscription, updateIssue } from '../services/dataService';
import { MessageSquare, Search, ExternalLink, Eye, Filter, FilterX } from 'lucide-react';
import { PageHeader } from '../components/layout';
import { Button, Input, Select, Card, Badge, Spinner, EmptyState, Modal, ModalFooter } from '../components/ui';
import ResizableSortableTableHeader from '../components/ResizableSortableTableHeader';

const STATUS_OPTIONS = ['Pendiente', 'Necesito Info', 'Resuelto'];

function ProductsTickets() {
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const [rows, setRows] = useState([]);
  const [isLoading, setIsLoading] = useState(true);  // Start true to show spinner on initial load
  const [apiError, setApiError] = useState(false);
  const [searchInput, setSearchInput] = useState('');
  
  // Modal state
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalDraft, setModalDraft] = useState({ status: '', notes: '' });
  const [isSaving, setIsSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');

  // Column width and sorting state
  const [columnWidths, setColumnWidths] = useState(() => {
    const saved = localStorage.getItem('productsTickets_columnWidths');
    return saved ? JSON.parse(saved) : {
      created: 180,
      upc: 150,
      description: 400,
      comment: 300,
      requestedBy: 200,
      status: 150,
      actions: 120,
    };
  });

  const [sortConfig, setSortConfig] = useState({ key: 'created', direction: 'desc' });

  // Filter state - hide resolved tickets by default
  const [hideResolved, setHideResolved] = useState(() => {
    const saved = localStorage.getItem('productsTickets_hideResolved');
    return saved !== null ? JSON.parse(saved) : true; // Default: hide resolved
  });

  // Persist filter preference
  useEffect(() => {
    localStorage.setItem('productsTickets_hideResolved', JSON.stringify(hideResolved));
  }, [hideResolved]);

  useEffect(() => {
    // Wait for auth to settle before firing a request without a bearer
    if (authLoading) {
      return;
    }

    // Auth done but not authenticated - stop loading, don't fetch
    if (!isAuthenticated) {
      setIsLoading(false);
      return;
    }

    let cleanup = null;

    (async () => {
      setIsLoading(true);
      setApiError(false);

      try {
        const fetched = await fetchIssues(['pm_comment']);

        if (fetched === null) {
          setApiError(true);
        } else {
          setRows(fetched);
          cleanup = createSubscription(
            'swissues',
            (newData) => setRows(newData),
            () => fetchIssues(['pm_comment'])
          );
        }
      } catch (err) {
        console.error('[ProductsTickets] API load error:', err);
        setApiError(true);
      } finally {
        setIsLoading(false);
      }
    })();

    return () => {
      if (cleanup) cleanup();
    };
  }, [authLoading, isAuthenticated]);

  // Persist column widths to localStorage
  useEffect(() => {
    localStorage.setItem('productsTickets_columnWidths', JSON.stringify(columnWidths));
  }, [columnWidths]);

  const filteredRows = useMemo(() => {
    let filtered = rows;

    // Filter out resolved tickets if hideResolved is true
    if (hideResolved) {
      filtered = filtered.filter((row) => {
        const status = row.resolutionStatus || '';
        // Hide both old "Created" status and new "Resuelto" status
        return status !== 'Created' && status !== 'Resuelto';
      });
    }

    // Apply search filter
    if (searchInput.trim()) {
      const query = searchInput.toLowerCase();
      filtered = filtered.filter((row) => {
        const created = row.createdAt ? new Date(row.createdAt).toLocaleString().toLowerCase() : '';
        return (
          (row.upc || '').toLowerCase().includes(query) ||
          (row.description || '').toLowerCase().includes(query) ||
          (row.comment || '').toLowerCase().includes(query) ||
          (row.userEmail || '').toLowerCase().includes(query) ||
          (row.resolutionStatus || '').toLowerCase().includes(query) ||
          (row.resolutionNotes || '').toLowerCase().includes(query) ||
          created.includes(query)
        );
      });
    }

    return filtered;
  }, [rows, searchInput, hideResolved]);

  const sortedRows = useMemo(() => {
    const sorted = [...filteredRows];
    
    sorted.sort((a, b) => {
      let aVal, bVal;
      
      switch (sortConfig.key) {
        case 'created':
          aVal = a.createdAt ? new Date(a.createdAt).getTime() : 0;
          bVal = b.createdAt ? new Date(b.createdAt).getTime() : 0;
          break;
        case 'upc':
          aVal = (a.upc || '').toLowerCase();
          bVal = (b.upc || '').toLowerCase();
          break;
        case 'description':
          aVal = (a.description || '').toLowerCase();
          bVal = (b.description || '').toLowerCase();
          break;
        case 'comment':
          aVal = (a.comment || '').toLowerCase();
          bVal = (b.comment || '').toLowerCase();
          break;
        case 'requestedBy':
          aVal = (a.userEmail || '').toLowerCase();
          bVal = (b.userEmail || '').toLowerCase();
          break;
        case 'status':
          aVal = (a.resolutionStatus || '').toLowerCase();
          bVal = (b.resolutionStatus || '').toLowerCase();
          break;
        default:
          return 0;
      }
      
      if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
    
    return sorted;
  }, [filteredRows, sortConfig]);

  const handleColumnResize = (columnKey, newWidth) => {
    setColumnWidths((prev) => ({
      ...prev,
      [columnKey]: Math.max(80, newWidth),
    }));
  };

  const handleSort = (columnKey) => {
    setSortConfig((prev) => ({
      key: columnKey,
      direction: prev.key === columnKey && prev.direction === 'asc' ? 'desc' : 'asc',
    }));
  };

  const handleRowClick = (row) => {
    setSelectedTicket(row);
    setModalDraft({
      status: row.resolutionStatus || '',
      notes: row.resolutionNotes || '',
    });
    setIsModalOpen(true);
    setSaveMessage('');
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setSelectedTicket(null);
    setModalDraft({ status: '', notes: '' });
    setSaveMessage('');
  };

  const handleModalSave = async () => {
    if (!selectedTicket) return;

    setIsSaving(true);
    setSaveMessage('');

    const { error } = await updateIssue(selectedTicket.id, {
      resolution_status: modalDraft.status,
      resolution_notes: modalDraft.notes,
    });

    if (error) {
      setSaveMessage('Error al guardar. Por favor, inténtalo de nuevo.');
      setIsSaving(false);
      return;
    }

    setRows((prev) =>
      prev.map((row) =>
        row.id === selectedTicket.id
          ? {
              ...row,
              resolutionStatus: modalDraft.status,
              resolutionNotes: modalDraft.notes,
            }
          : row
      )
    );

    setSaveMessage('Guardado exitosamente');
    setIsSaving(false);
    
    // Close modal after short delay
    setTimeout(() => {
      handleModalClose();
    }, 1000);
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'Resuelto':
        return <Badge variant="success">Resuelto</Badge>;
      case 'Necesito Info':
        return <Badge variant="primary">Necesito Info</Badge>;
      case 'Pendiente':
        return <Badge variant="warning">Pendiente</Badge>;
      default:
        return <Badge variant="default">{status || '-'}</Badge>;
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Products Tickets"
        description="Comment tickets created from Products Manager (source: pm_comment)"
        actions={
          <Badge variant="primary" className="text-sm">
            Total: {filteredRows.length}
          </Badge>
        }
      />

      {/* Search and Filter */}
      <Card className="p-4">
        <div className="flex items-center gap-3">
          <div className="flex-1 max-w-xl">
            <Input
              placeholder="Search by UPC, description, comment, status, notes, or user"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              leadingIcon={<Search size={18} />}
            />
          </div>
          <Button
            variant={hideResolved ? "primary" : "secondary"}
            size="md"
            onClick={() => setHideResolved(!hideResolved)}
            leftIcon={hideResolved ? <Filter size={18} /> : <FilterX size={18} />}
          >
            {hideResolved ? 'Hiding Resolved' : 'Showing All'}
          </Button>
        </div>
      </Card>

      {/* Tickets Table */}
      {isLoading ? (
        <Card className="flex items-center justify-center h-64">
          <Spinner size="lg" label="Loading product tickets..." />
        </Card>
      ) : apiError ? (
        <Card className="flex items-center justify-center h-64 bg-error-50 border-error-200">
          <p className="text-error-700 font-medium">Could not load product tickets from the API.</p>
        </Card>
      ) : sortedRows.length === 0 ? (
        <Card>
          <EmptyState
            icon={<MessageSquare />}
            title="No product tickets found"
            description={searchInput ? 'Try adjusting your search' : 'Tickets will appear here when created from Products Manager'}
          />
        </Card>
      ) : (
        <Card noPadding className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full table-fixed">
              <thead className="bg-neutral-50 border-b border-neutral-200">
                <tr>
                  <ResizableSortableTableHeader
                    columnKey="created"
                    label="Created"
                    width={columnWidths.created}
                    sortConfig={sortConfig}
                    onSort={handleSort}
                    onResize={handleColumnResize}
                  />
                  <ResizableSortableTableHeader
                    columnKey="upc"
                    label="UPC"
                    width={columnWidths.upc}
                    sortConfig={sortConfig}
                    onSort={handleSort}
                    onResize={handleColumnResize}
                  />
                  <ResizableSortableTableHeader
                    columnKey="description"
                    label="Description"
                    width={columnWidths.description}
                    sortConfig={sortConfig}
                    onSort={handleSort}
                    onResize={handleColumnResize}
                  />
                  <ResizableSortableTableHeader
                    columnKey="comment"
                    label="Comment"
                    width={columnWidths.comment}
                    sortConfig={sortConfig}
                    onSort={handleSort}
                    onResize={handleColumnResize}
                  />
                  <ResizableSortableTableHeader
                    columnKey="requestedBy"
                    label="Requested By"
                    width={columnWidths.requestedBy}
                    sortConfig={sortConfig}
                    onSort={handleSort}
                    onResize={handleColumnResize}
                  />
                  <ResizableSortableTableHeader
                    columnKey="status"
                    label="Status"
                    width={columnWidths.status}
                    sortConfig={sortConfig}
                    onSort={handleSort}
                    onResize={handleColumnResize}
                  />
                  <ResizableSortableTableHeader
                    columnKey="actions"
                    label="Actions"
                    width={columnWidths.actions}
                    sortConfig={sortConfig}
                    onSort={handleSort}
                    onResize={handleColumnResize}
                    sortable={false}
                  />
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-100">
                {sortedRows.map((row) => {
                  return (
                    <tr 
                      key={row.id} 
                      className="hover:bg-neutral-50 cursor-pointer transition-colors"
                      onClick={() => handleRowClick(row)}
                    >
                      <td className="px-4 py-3 text-sm text-neutral-700" style={{ width: `${columnWidths.created}px` }}>
                        {row.createdAt ? new Date(row.createdAt).toLocaleString() : '-'}
                      </td>
                      <td className="px-4 py-3 text-sm font-mono" style={{ width: `${columnWidths.upc}px` }}>
                        {row.upc ? (
                          <a
                            href={`https://us-east-1.online.tableau.com/#/site/reesa/views/BuscadorREESA2025_4/HistorialParticipaciondelProducto?SelectedProduct=${row.upc}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-primary-600 hover:text-primary-700 hover:underline inline-flex items-center gap-1"
                            onClick={(e) => e.stopPropagation()}
                          >
                            {row.upc}
                            <ExternalLink size={12} />
                          </a>
                        ) : (
                          '-'
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-neutral-700" style={{ width: `${columnWidths.description}px` }}>
                        <div className="line-clamp-3">{row.description || '-'}</div>
                      </td>
                      <td className="px-4 py-3 text-sm text-neutral-700" style={{ width: `${columnWidths.comment}px` }}>
                        <div className="line-clamp-3">{row.comment || '-'}</div>
                      </td>
                      <td className="px-4 py-3 text-sm text-neutral-700" style={{ width: `${columnWidths.requestedBy}px` }}>
                        {row.userEmail || '-'}
                      </td>
                      <td className="px-4 py-3" style={{ width: `${columnWidths.status}px` }}>
                        {getStatusBadge(row.resolutionStatus)}
                      </td>
                      <td className="px-4 py-3" style={{ width: `${columnWidths.actions}px` }}>
                        <Button
                          size="sm"
                          variant="ghost"
                          leftIcon={<Eye size={14} />}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRowClick(row);
                          }}
                        >
                          View
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Detail Modal */}
      <Modal
        open={isModalOpen}
        onClose={handleModalClose}
        title="Ticket Details"
        size="lg"
      >
        {selectedTicket && (
          <div className="space-y-4">
            {/* Read-only fields */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-semibold text-neutral-600 uppercase mb-1">
                  Created
                </label>
                <p className="text-sm text-neutral-700">
                  {selectedTicket.createdAt ? new Date(selectedTicket.createdAt).toLocaleString() : '-'}
                </p>
              </div>
              <div>
                <label className="block text-xs font-semibold text-neutral-600 uppercase mb-1">
                  UPC
                </label>
                {selectedTicket.upc ? (
                  <a
                    href={`https://us-east-1.online.tableau.com/#/site/reesa/views/BuscadorREESA2025_4/HistorialParticipaciondelProducto?SelectedProduct=${selectedTicket.upc}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm font-mono text-primary-600 hover:text-primary-700 hover:underline inline-flex items-center gap-1"
                  >
                    {selectedTicket.upc}
                    <ExternalLink size={14} />
                  </a>
                ) : (
                  <p className="text-sm text-neutral-700">-</p>
                )}
              </div>
            </div>

            <div>
              <label className="block text-xs font-semibold text-neutral-600 uppercase mb-1">
                Description
              </label>
              <p className="text-sm text-neutral-700">
                {selectedTicket.description || '-'}
              </p>
            </div>

            <div>
              <label className="block text-xs font-semibold text-neutral-600 uppercase mb-1">
                Comment
              </label>
              <p className="text-sm text-neutral-700">
                {selectedTicket.comment || '-'}
              </p>
            </div>

            <div>
              <label className="block text-xs font-semibold text-neutral-600 uppercase mb-1">
                Requested By
              </label>
              <p className="text-sm text-neutral-700">
                {selectedTicket.userEmail || '-'}
              </p>
            </div>

            {/* Editable fields */}
            <div className="pt-4 border-t border-neutral-200">
              <h4 className="text-sm font-semibold text-neutral-900 mb-3">Resolution</h4>
              
              <div className="space-y-3">
                <div>
                  <label className="block text-xs font-semibold text-neutral-600 uppercase mb-1">
                    Status
                  </label>
                  <Select
                    value={modalDraft.status}
                    onChange={(e) => setModalDraft(prev => ({ ...prev, status: e.target.value }))}
                    disabled={isSaving}
                    options={[
                      { value: '', label: '-' },
                      ...STATUS_OPTIONS.map((status) => ({ value: status, label: status }))
                    ]}
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-neutral-600 uppercase mb-1">
                    Notes
                  </label>
                  <textarea
                    value={modalDraft.notes}
                    onChange={(e) => setModalDraft(prev => ({ ...prev, notes: e.target.value }))}
                    disabled={isSaving}
                    rows={4}
                    className="w-full px-3 py-2 border border-neutral-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-60"
                    placeholder="Add resolution notes..."
                  />
                </div>
              </div>
            </div>

            {/* Save message */}
            {saveMessage && (
              <div className={`text-sm text-center py-2 px-3 rounded-md ${
                saveMessage.includes('Error') || saveMessage.includes('Error') 
                  ? 'bg-error-50 text-error-700' 
                  : 'bg-success-50 text-success-700'
              }`}>
                {saveMessage}
              </div>
            )}

            {/* Footer */}
            <ModalFooter>
              <Button
                variant="secondary"
                onClick={handleModalClose}
                disabled={isSaving}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={handleModalSave}
                loading={isSaving}
              >
                Save Changes
              </Button>
            </ModalFooter>
          </div>
        )}
      </Modal>
    </div>
  );
}

export default ProductsTickets;
